import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "components/AppIcon";
import Header from "../../layout/Header";
import FilterToolbar from "./components/FilterToolbar";
import Pagination from "./components/Pagination";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteClientService,
  getClientsService,
} from "redux/services/clients_service";
import { toast } from "react-toastify";
import TrainersTable from "./components/TrainersTable";
import {
  deleteTrainerService,
  getTrainersService,
} from "redux/services/trainers_service";
import { useLanguage } from "../../LanguageContext";

const translations = {
  en: {
    pageTitle: "Trainer Management",
    addNewTrainer: "Add New Trainer",
    totalTrainers: "Total trainers",
    successMessage: "Success",
  },
  ar: {
    pageTitle: "إدارة المدربين",
    addNewTrainer: "إضافة مدرب جديد",
    totalTrainers: "إجمالي المدربين",
    successMessage: "نجاح",
  },
};

const TrainersManagement = () => {
  const dispatch = useDispatch();
  const { language } = useLanguage();

  const permissions = JSON.parse(localStorage.getItem("permissions"));

  const addTrainserExists = permissions.some(
    (permission) => permission.name === "create_trainer"
  );

  const viewTrainerExists = permissions.some(
    (permission) => permission.name === "view_trainer"
  );

  const [selectedClients, setSelectedClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    status: "All",
    dateRange: { start: "", end: "" },
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { trainersList, metaTrainers } = useSelector((state) => state.trainers);

  // Mock client data
  const mockClients = [
    // ... (mock client data remains unchanged)
  ];

  const itemsPerPage = 6;

  // Filter clients based on search and status
  const filteredClients = useMemo(() => {
    return mockClients.filter((client) => {
      const matchesSearch =
        client.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        client.phone.includes(filters.search) ||
        client.email.toLowerCase().includes(filters.search.toLowerCase());

      const matchesStatus =
        filters.status === "All" ||
        client.subscriptionStatus === filters.status;

      let matchesDateRange = true;
      if (filters.dateRange.start && filters.dateRange.end) {
        const clientStartDate = new Date(client.startDate);
        const filterStartDate = new Date(filters.dateRange.start);
        const filterEndDate = new Date(filters.dateRange.end);
        matchesDateRange =
          clientStartDate >= filterStartDate &&
          clientStartDate <= filterEndDate;
      }

      return matchesSearch && matchesStatus && matchesDateRange;
    });
  }, [filters]);

  // Paginate filtered clients
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredClients.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredClients, currentPage]);

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  const handlePageChange = (page) => {
    console.log(`Changing to page: ${page}`);
    setCurrentPage(page);
    dispatch(getTrainersService({ page: page }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedClients(paginatedClients.map((client) => client.id));
    } else {
      setSelectedClients([]);
    }
  };

  const handleSelectClient = (clientId, checked) => {
    if (checked) {
      setSelectedClients((prev) => [...prev, clientId]);
    } else {
      setSelectedClients((prev) => prev.filter((id) => id !== clientId));
    }
  };

  const handleDeleteClient = (clientId) => {
    dispatch(deleteTrainerService(clientId)).then((response) => {
      if (response.payload?.status === 200) {
        toast.success(
          language === "ar" ? "تم الحذف بنجاح" : "Deleted successfully"
        );

        dispatch(getTrainersService({ page: 1 }));
      }
    });
  };

  const handleRenewSubscription = (clientId) => {
    console.log("Renewing subscription for client:", clientId);
  };

  const handleBulkAction = (action) => {
    console.log(`Performing ${action} on clients:`, selectedClients);
    if (action === "delete") {
      setSelectedClients([]);
    }
  };

  useEffect(() => {
    dispatch(getTrainersService({ page: 1 }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-12">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                {translations[language].pageTitle}
              </h1>
            </div>
            {addTrainserExists && (
              <Link
                to="/add-edit-trainer-profile"
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors duration-200"
              >
                <Icon name="Plus" size={20} className="mr-2 ml-2" />
                {translations[language].addNewTrainer}
              </Link>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Icon name="Users" size={24} className="text-green-600" />
              </div>
              <div className="ml-3 mr-3">
                <p className="text-sm font-medium text-gray-600">
                  {translations[language].totalTrainers}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {metaTrainers?.total}
                </p>
              </div>
            </div>
          </div>
        </div>

        <FilterToolbar
          filters={filters}
          onFiltersChange={setFilters}
          language={language}
        />

        {viewTrainerExists && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <TrainersTable
              clients={trainersList}
              selectedClients={selectedClients}
              onSelectAll={handleSelectAll}
              onSelectClient={handleSelectClient}
              onDeleteClient={handleDeleteClient}
              onRenewSubscription={handleRenewSubscription}
              allSelected={
                selectedClients.length === paginatedClients.length &&
                paginatedClients.length > 0
              }
            />

            {metaTrainers?.total > 9 && (
              <Pagination
                currentPage={metaTrainers?.current_page}
                totalPages={metaTrainers?.last_page}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainersManagement;
